import React from 'react';
import { Shield, Lock, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#ECEC75] via-[#e6e67c] to-[#ECEC75] py-24">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-black rounded-full mb-6">
            <Shield size={40} className="text-[#D4AF37]" />
          </div>
          <h1 className="text-5xl font-bold text-black mb-4" style={{ fontFamily: 'Crimson Text, serif' }}>
            Politique de Confidentialité
          </h1>
          <p className="text-xl text-black/70">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
          </p>
        </div>

        {/* Content */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-8 md:p-12 space-y-8">
          <div className="prose prose-lg max-w-none">
            <div className="flex items-start space-x-4 p-6 bg-[#D4AF37]/10 rounded-xl mb-8">
              <Lock className="text-[#D4AF37] flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className="text-xl font-bold text-black mb-2">Vos données sont respectées</h3>
                <p className="text-black/70">
                  Le Cosmos Tacos utilise uniquement des outils sécurisés pour améliorer votre expérience.
                  Nous ne vendons jamais vos données personnelles.
                </p>
              </div>
            </div>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-black mb-4">1. Collecte des informations</h2>
              <p className="text-black/80 leading-relaxed mb-4">
                Nous collectons les informations que vous nous fournissez directement :
              </p>
              <ul className="list-disc list-inside space-y-2 text-black/80 ml-4">
                <li>Nom, email et téléphone (formulaire de contact)</li>
                <li>Données de navigation (pages visitées, durée)</li>
                <li>Informations d'appareil (type, système d'exploitation)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-black mb-4">2. Utilisation des données</h2>
              <p className="text-black/80 leading-relaxed mb-4">
                Vos informations nous permettent de :
              </p>
              <ul className="list-disc list-inside space-y-2 text-black/80 ml-4">
                <li>Répondre à vos demandes de contact</li>
                <li>Améliorer notre site et nos services</li>
                <li>Analyser les performances du site</li>
                <li>Personnaliser votre expérience utilisateur</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-black mb-4">3. Cookies et technologies similaires</h2>
              <div className="flex items-start space-x-3 p-4 bg-black/5 rounded-lg mb-4">
                <Eye className="text-black flex-shrink-0 mt-1" size={20} />
                <p className="text-black/80 leading-relaxed">
                  Nous utilisons des cookies pour améliorer votre navigation. Consultez notre 
                  <Link to="/cookies" className="text-[#D4AF37] hover:underline ml-1">politique des cookies</Link> 
                  pour plus de détails.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-black mb-4">4. Partage des données</h2>
              <p className="text-black/80 leading-relaxed mb-4">
                Nous ne partageons vos données qu'avec :
              </p>
              <ul className="list-disc list-inside space-y-2 text-black/80 ml-4">
                <li><strong>Plateformes de livraison :</strong> Uber Eats, Deliveroo (pour vos commandes)</li>
                <li><strong>Outils d'analyse :</strong> Pour comprendre l'utilisation du site</li>
                <li><strong>Services légaux :</strong> Si la loi l'exige</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-black mb-4">5. Vos droits</h2>
              <p className="text-black/80 leading-relaxed mb-4">
                Conformément au RGPD, vous disposez des droits suivants :
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-[#D4AF37]/5 rounded-lg">
                  <h4 className="font-bold text-black mb-2">✓ Droit d'accès</h4>
                  <p className="text-sm text-black/70">Consulter vos données</p>
                </div>
                <div className="p-4 bg-[#D4AF37]/5 rounded-lg">
                  <h4 className="font-bold text-black mb-2">✓ Droit de rectification</h4>
                  <p className="text-sm text-black/70">Corriger vos informations</p>
                </div>
                <div className="p-4 bg-[#D4AF37]/5 rounded-lg">
                  <h4 className="font-bold text-black mb-2">✓ Droit à l'effacement</h4>
                  <p className="text-sm text-black/70">Supprimer vos données</p>
                </div>
                <div className="p-4 bg-[#D4AF37]/5 rounded-lg">
                  <h4 className="font-bold text-black mb-2">✓ Droit d'opposition</h4>
                  <p className="text-sm text-black/70">Refuser le traitement</p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-black mb-4">6. Sécurité</h2>
              <p className="text-black/80 leading-relaxed">
                Nous mettons en œuvre des mesures de sécurité appropriées pour protéger vos données contre
                tout accès non autorisé, modification, divulgation ou destruction.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-black mb-4">7. Contact</h2>
              <p className="text-black/80 leading-relaxed mb-4">
                Pour toute question concernant cette politique de confidentialité ou pour exercer vos droits :
              </p>
              <div className="p-6 bg-black text-white rounded-xl">
                <p className="font-bold mb-2">Le Cosmos Tacos</p>
                <p>49 Av. de la Libération, 42000 Saint-Étienne</p>
                <p>Tél : 04 87 66 22 41</p>
              </div>
            </section>
          </div>
        </div>

        {/* Back Button */}
        <div className="text-center mt-8">
          <Link
            to="/"
            className="inline-block bg-black text-white px-8 py-3 rounded-lg font-semibold hover:bg-black/90 transition-colors"
          >
            ← Retour à l'accueil
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
